//Deprecated - Use FormatterContext

import { defaultLocaleFormatters } from '../constants/locale';

export const formatCurrency = (
    value: number | undefined | null,
    currency?: string | null | undefined,
    country?: string | null | undefined
): string => {
    if (value === undefined || value === null || isNaN(value)) {
        value = 0;
    }
    
    const currencyCode = currency || 'EUR';
    const countryCode = country || defaultLocaleFormatters;

    return new Intl.NumberFormat(countryCode, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export const formatPercentage = (value: number): string => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${(value * 100).toFixed(2)}%`;
};

export const formatNumber = (value: number, country?: string | null | undefined): string => {
    const countryCode = country || defaultLocaleFormatters;
    return new Intl.NumberFormat(countryCode, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
};

export const formatCrypto = (value: number, country?: string | null | undefined): string => {
    const countryCode = country || defaultLocaleFormatters;
    return new Intl.NumberFormat(countryCode, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 8,
        useGrouping: true,
    }).format(value);
};

const isUnixTimestamp = (timestamp: number): boolean => {
    // A Unix timestamp in seconds will be a 10-digit number
    // A Unix timestamp in milliseconds will be a 13-digit number
    return timestamp.toString().length <= 10;
};

/**
 * Format a timestamp as a localized "time ago" string using translation keys
 * @param timestamp The timestamp to format (either unix seconds or milliseconds)
 * @param t The translation function
 * @returns Translated time ago string
 */
export const formatTimeAgo = (timestamp: number, t: any): string => {
    // Ensure timestamp is in milliseconds
    const timestampMs = isUnixTimestamp(timestamp) ? timestamp * 1000 : timestamp;
    const now = Date.now();
    const diffInSeconds = Math.floor((now - timestampMs) / 1000);

    // Use direct string translation for German pluralization
    // German translation keys will be properly formatted based on count
    // The t function should automatically select the right form based on count

    // Less than a minute
    if (diffInSeconds < 60) {
        return diffInSeconds < 5 
            ? t('common.timeAgo.lessThanXSeconds', { count: 5 })
            : t('common.timeAgo.xSeconds', { count: diffInSeconds });
    }
    
    // Less than an hour
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        // For plural forms, we use the same key but the count determines if plural or singular is used
        return t('common.timeAgo.xMinutes', { count: diffInMinutes });
    }
    
    // Less than a day
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return t('common.timeAgo.xHours', { count: diffInHours });
    }
    
    // Less than a month (30 days)
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
        return t('common.timeAgo.xDays', { count: diffInDays });
    }
    
    // Less than a year
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return t('common.timeAgo.xMonths', { count: diffInMonths });
    }
    
    // More than a year
    const diffInYears = Math.floor(diffInDays / 365);
    return t('common.timeAgo.xYears', { count: diffInYears });
};

export const formatDate = (timestamp: number, country?: string | null | undefined) => {
    const countryCode = country || defaultLocaleFormatters;
    try {
        if (!timestamp || isNaN(timestamp)) {
            console.warn('Invalid timestamp:', timestamp);
            return new Date().toLocaleString(countryCode);
        }

        const date = new Date(isUnixTimestamp(timestamp) ? timestamp * 1000 : timestamp);
        return date.toLocaleString(countryCode, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch (error) {
        console.error('Error formatting date:', error, 'timestamp:', timestamp);
        return new Date().toLocaleString(countryCode);
    }
};

export const formatDateTwoLines = (timestamp: number, countryCode: string = 'de-DE') => {
    try {
        if (!timestamp || isNaN(timestamp)) {
            console.warn('Invalid timestamp:', timestamp);
            const now = new Date();
            return {
                date: now.toLocaleDateString(countryCode),
                time: now.toLocaleTimeString(countryCode),
            };
        }

        const date = new Date(isUnixTimestamp(timestamp) ? timestamp * 1000 : timestamp);
        return {
            date: date.toLocaleDateString(countryCode, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }),
            time: date.toLocaleTimeString(countryCode, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            }),
        };
    } catch (error) {
        console.error('Error formatting date:', error, 'timestamp:', timestamp);
        const now = new Date();
        return {
            date: now.toLocaleDateString(countryCode),
            time: now.toLocaleTimeString(countryCode),
        };
    }
};
